import {toIntent} from '@sail/engine';
import type {Intent} from '@sail/engine';
import {createMotionProps, runMotionIntents} from './util';
import {isMotionProps, mergeMotionArgs} from './util/resolveMotionTokens';
import {RECIPES} from './util/recipeMap';
import type {RecipeName} from './util/recipeMap';
import type {MotionProps} from './types';

type MotionArgs =
  | [MotionProps]
  | [RecipeName, ...RecipeName[]]
  | [...RecipeName[], MotionProps];

/**
 * Adds animations or gestures to any Sail view.
 *
 * ```tsx
 * // Raw props
 * <view.div uses={[motion({animate: {rotate: 45}})]} />
 *
 * // Named recipe
 * <view.div uses={[motion('fade')]} />
 *
 * // Combined recipes
 * <view.div uses={[motion('fade', 'spring')]} />
 *
 * // Recipe with overrides
 * <view.div
 *   uses={[
 *     motion('fade', {
 *       animate: {transition: {duration: 200}},
 *       exit: {transition: {duration: 100}},
 *     }),
 *   ]}
 * />
 * ```
 *
 * The `motion` API accepts the same props that power [Framer Motion's components](https://www.framer.com/motion/component/). However, instead of creating a new element, the `motion` API adds those animations and gestures to a Sail view.
 *
 * Motion intents can be provided to views via the `uses` prop anywhere intents are accepted, including within `createView` and `customize`. When a view has multiple motion intents, they will automatically be merged into a single animation.
 *
 * @see https://sail.corp.stripe.com/next/beta/apis/motion/intent
 */
export function motion(...args: MotionArgs): Intent {
  const props =
    args.length === 1 && isMotionProps(args[0])
      ? args[0]
      : mergeMotionArgs(args, RECIPES);
  return toIntent([createMotionProps(props), runMotionIntents]);
}
